import React from 'react'

/**
 * ErrorBoundary — Developer-facing crash handler
 *
 * Pass `resetKey` (e.g. `${site}/${lang}`) so the boundary remounts / clears
 * when navigating between Light Call subsites — otherwise a prior crash
 * stays on screen forever for the same Route element.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, errorInfo: null })
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-color-bg-light bg-paper px-6 py-12 font-primary text-color-text">
          <div className="max-w-content mx-auto">
            <div className="rounded-2xl border border-color-primary bg-white p-8 md:p-10 shadow-lg">
              <h1 className="typography-h2 mb-4">
                Something went wrong
              </h1>

              <h2 className="typography-body-lg text-color-primary mb-6">
                {this.state.error?.toString()}
              </h2>

              <details className="mt-6">
                <summary className="cursor-pointer typography-label">
                  Stack trace
                </summary>
                <pre className="mt-3 p-4 rounded-lg bg-color-bg-light text-color-text font-mono text-xs leading-relaxed overflow-auto">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>

              <button
                type="button"
                className="mt-8 typography-menu-text bg-color-primary text-primary-on-dark rounded-full px-8 py-3"
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null })
                  window.location.reload()
                }}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
